import {
  Alert,
  Box,
  Button,
  ContentLayout,
  Form,
  FormField,
  Header,
  Input,
  Multiselect,
  Select,
  SpaceBetween,
  Spinner,
} from '@cloudscape-design/components';
import MDEditor from '@uiw/react-md-editor';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { WikiPage, WikiPageUpdate } from '../../models/Wiki';
import { canEdit, canPublish, getWikiPage, updateWikiPage } from './Wiki.logic';
import { statusOptions, useSession, visibilityOptions } from './Wiki.shared';

const WikiEdit: React.FC = () => {
  const { slug = '' } = useParams();
  const { tenantId, role, userId } = useSession();
  const navigate = useNavigate();

  // ---- Core page/form state
  const [page, setPage] = useState<WikiPage | undefined>();
  const [form, setForm] = useState<WikiPageUpdate>({ slug });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // ---- Multiselect hooks (DECLARE BEFORE ANY EARLY RETURNS)
  const [tagOptions, setTagOptions] = useState<{ label: string; value: string }[]>([]);
  const [tagFilter, setTagFilter] = useState('');
  const selectedTagOptions = useMemo(() => (form.tags ?? []).map((v) => ({ label: v, value: v })), [form.tags]);
  const computedTagOptions = useMemo(() => {
    const baseVals = new Set([...(form.tags ?? []), ...tagOptions.map((o) => o.value)]);
    const base = Array.from(baseVals).map((v) => ({ label: v, value: v }));
    if (tagFilter && !Array.from(baseVals).some((v) => v.toLowerCase() === tagFilter.toLowerCase())) {
      base.unshift({ label: `Create "${tagFilter}"`, value: `__new__:${tagFilter}` });
    }
    return base;
  }, [tagOptions, tagFilter, form.tags]);

  const [categoryOptions, setCategoryOptions] = useState<{ label: string; value: string }[]>([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const selectedCategoryOptions = useMemo(
    () => (form.categories ?? []).map((v) => ({ label: v, value: v })),
    [form.categories],
  );
  const computedCategoryOptions = useMemo(() => {
    const baseVals = new Set([...(form.categories ?? []), ...categoryOptions.map((o) => o.value)]);
    const base = Array.from(baseVals).map((v) => ({ label: v, value: v }));
    if (categoryFilter && !Array.from(baseVals).some((v) => v.toLowerCase() === categoryFilter.toLowerCase())) {
      base.unshift({ label: `Create "${categoryFilter}"`, value: `__new__:${categoryFilter}` });
    }
    return base;
  }, [categoryOptions, categoryFilter, form.categories]);

  // ---- Load page
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const p = await getWikiPage(tenantId, slug);
        if (!mounted) return;
        setPage(p);
        if (p) {
          setForm({
            slug: p.slug, // immutable here
            title: p.title,
            contentMd: p.contentMd,
            status: p.status,
            visibility: p.visibility,
            pinned: p.pinned ?? false,
            tags: p.tags ?? [],
            categories: p.categories ?? [],
          });
        } else {
          setForm({ slug }); // ensure form has slug
        }
      } catch (e: any) {
        if (!mounted) return;
        setErr(e.message || String(e));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [tenantId, slug]);

  // ---- Seed multiselect options once page/form is known
  useEffect(() => {
    if (!page) return;
    if (page.tags?.length) {
      setTagOptions((opts) => {
        const current = new Set(opts.map((o) => o.value));
        page.tags!.forEach((v) => current.add(v));
        return Array.from(current).map((v) => ({ label: v, value: v }));
      });
    }
    if (page.categories?.length) {
      setCategoryOptions((opts) => {
        const current = new Set(opts.map((o) => o.value));
        page.categories!.forEach((v) => current.add(v));
        return Array.from(current).map((v) => ({ label: v, value: v }));
      });
    }
  }, [page]);

  // ---- Derived flags (not hooks)
  const editable = page ? canEdit(page, role, userId) : false;
  const publishable = canPublish(role);

  // ---- Early return AFTER all hooks are declared
  if (loading) {
    return (
      <ContentLayout header={<Header>Loading…</Header>}>
        <Spinner /> Loading…
      </ContentLayout>
    );
  }

  if (!page) {
    return (
      <ContentLayout header={<Header>Not found</Header>}>
        <Box>Page not found.</Box>
      </ContentLayout>
    );
  }

  async function onSave() {
    try {
      console.log('Saving wiki page...', form);
      setSaving(true);
      await updateWikiPage(
        tenantId,
        {
          ...form,
        },
        userId,
      );
      navigate(`/wiki/${slug}`);
    } catch (e: any) {
      setErr(e.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ContentLayout
      disableOverlap
      header={
        <Header
          variant="h1"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => navigate(-1)}>Cancel</Button>
              <Button variant="primary" loading={saving} disabled={!editable} onClick={onSave}>
                Save
              </Button>
            </SpaceBetween>
          }
        >
          Edit: {page.title}
        </Header>
      }
    >
      <Form>
        <SpaceBetween size="l">
          {err && (
            <Alert type="error" header="Error">
              {err}
            </Alert>
          )}

          <FormField label="Slug (immutable)">
            <Input value={page.slug} readOnly />
          </FormField>

          <FormField label="Title">
            <Input
              value={form.title ?? ''}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  title: e.detail.value,
                }))
              }
              readOnly={!editable}
            />
          </FormField>

          <FormField label="Visibility">
            <Select
              selectedOption={{
                value: form.visibility,
                label:
                  visibilityOptions.find((o) => o.value === form.visibility)?.label || String(form.visibility || ''),
              }}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  visibility: e.detail.selectedOption.value as any,
                }))
              }
              options={visibilityOptions as any}
              disabled={!editable}
            />
          </FormField>

          <FormField label="Status">
            <Select
              selectedOption={{
                value: form.status,
                label: statusOptions.find((o) => o.value === form.status)?.label || String(form.status || ''),
              }}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  status: e.detail.selectedOption.value as any,
                }))
              }
              options={statusOptions as any}
            />
          </FormField>

          <FormField label="Tags">
            <Multiselect
              selectedOptions={selectedTagOptions}
              options={computedTagOptions}
              placeholder="Add tags…"
              filteringType="auto"
              onLoadItems={({ detail }) => setTagFilter(detail.filteringText ?? '')}
              onChange={({ detail }) => {
                let vals = detail.selectedOptions.map((o) => String(o.value));
                const created = vals.filter((v) => v.startsWith('__new__:'));
                if (created.length) {
                  const pure = created.map((v) => v.slice(8));
                  setTagOptions((opts) => {
                    const current = new Set(opts.map((o) => o.value));
                    pure.forEach((v) => current.add(v));
                    return Array.from(current).map((v) => ({ label: v, value: v }));
                  });
                  vals = vals.map((v) => (v.startsWith('__new__:') ? v.slice(8) : v));
                }
                setForm((f) => ({ ...f, tags: vals }));
              }}
              disabled={!editable}
            />
          </FormField>

          <FormField label="Categories">
            <Multiselect
              selectedOptions={selectedCategoryOptions}
              options={computedCategoryOptions}
              placeholder="Add categories…"
              filteringType="auto"
              onLoadItems={({ detail }) => setCategoryFilter(detail.filteringText ?? '')}
              onChange={({ detail }) => {
                let vals = detail.selectedOptions.map((o) => String(o.value));
                const created = vals.filter((v) => v.startsWith('__new__:'));
                if (created.length) {
                  const pure = created.map((v) => v.slice(8));
                  setCategoryOptions((opts) => {
                    const current = new Set(opts.map((o) => o.value));
                    pure.forEach((v) => current.add(v));
                    return Array.from(current).map((v) => ({ label: v, value: v }));
                  });
                  vals = vals.map((v) => (v.startsWith('__new__:') ? v.slice(8) : v));
                }
                setForm((f) => ({ ...f, categories: vals }));
              }}
              disabled={!editable}
            />
          </FormField>

          <FormField label="Content (Markdown)">
            <div data-color-mode="light">
              <MDEditor
                value={form.contentMd ?? ''}
                onChange={(v) =>
                  setForm((f) => ({
                    ...f,
                    contentMd: v || '',
                  }))
                }
                preview="live"
                height={420}
              />
            </div>
          </FormField>
        </SpaceBetween>
      </Form>
    </ContentLayout>
  );
};

export default WikiEdit;
